import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    listarGastosMedicos,
    eliminarGastoMedico
} from '../../services/gastoMedicoService';
import { listarMedicos } from '../../services/medicoService';
import { getTramites } from '../../services/tramiteService';
import { getClientes } from '../../services/clienteService';
import ModalAgregarGastoMedico from '../../components/GastosMedicosComponentes/ModalAgregarGastoMedico';
import ModalEditarGastoMedico from '../../components/GastosMedicosComponentes/ModalEditarGastoMedico';

import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar,
    Alert
} from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PageHeader from '../../components/ui/PageHeader';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FolderIcon from '@mui/icons-material/Folder';

function ListadoGastoMedico() {
    const [gastos, setGastos] = useState([]);
    const [gastosAgrupados, setGastosAgrupados] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mesActual, setMesActual] = useState({ nombre: '', monto: 0, cantidad: 0 });
    const [mesesExpandidos, setMesesExpandidos] = useState({});
    const [dialogoEliminar, setDialogoEliminar] = useState({ open: false, idGastomedico: null });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [medicos, setMedicos] = useState([]);
    const [tramites, setTramites] = useState([]);
    const [clientes, setClientes] = useState([]);

    const [modalAbierto, setModalAbierto] = useState(false);
    const [modalEditarAbierto, setModalEditarAbierto] = useState(false);
    const [gastoSeleccionado, setGastoSeleccionado] = useState(null);

    const [filtroAnio, setFiltroAnio] = useState('Todos');
    const [filtroMes, setFiltroMes] = useState('Todos');


    const navigate = useNavigate();

    const normalizarArray = (valor) => {
        if (Array.isArray(valor)) return valor;
        if (valor && Array.isArray(valor.content)) return valor.content;
        if (valor && Array.isArray(valor.data)) return valor.data;
        return [];
    };

    // Formatear fecha
    const formatearFecha = (fecha) => {
        return new Date(fecha).toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Obtener nombre del mes
    const obtenerNombreMes = (fecha) => {
        return new Date(fecha).toLocaleDateString('es-ES', {
            month: 'long',
            year: 'numeric'
        });
    };

    // Obtener clave del mes (YYYY-MM)
    const obtenerClaveMes = (fecha) => {
        const date = new Date(fecha);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    };

    // Obtener clave del mes actual
    const obtenerClaveMesActual = () => {
        const ahora = new Date();
        return `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}`;
    };

    // Obtener nombre del mes actual
    const obtenerNombreMesActual = () => {
        return new Date().toLocaleDateString('es-ES', {
            month: 'long',
            year: 'numeric'
        });
    };

    const mesesNombres = [
        { val: '01', label: 'Enero' }, { val: '02', label: 'Febrero' },
        { val: '03', label: 'Marzo' }, { val: '04', label: 'Abril' },
        { val: '05', label: 'Mayo' }, { val: '06', label: 'Junio' },
        { val: '07', label: 'Julio' }, { val: '08', label: 'Agosto' },
        { val: '09', label: 'Septiembre' }, { val: '10', label: 'Octubre' },
        { val: '11', label: 'Noviembre' }, { val: '12', label: 'Diciembre' }
    ];

    // Obtener nombre del médico por ID
    // Obtener nombre del médico por ID
    const obtenerNombreMedico = (idMedico) => {
        // Si el ID es null, undefined o 0, devolvemos el guion inmediatamente
        if (!idMedico) return "-";

        const medicosArray = normalizarArray(medicos);
        const medico = medicosArray.find(m => m.idMedico === idMedico);
        return medico ? `${medico.nombre} ${medico.apellido}` : "-";
    };

    // Obtener nombre del cliente por ID de trámite
    const obtenerNombreClientePorTramite = (idTramite) => {
        const tramitesArray = normalizarArray(tramites);
        const clientesArray = normalizarArray(clientes);

        const tramite = tramitesArray.find(t => t.idTramite === idTramite);
        if (!tramite) return `Trámite #${idTramite}`;

        // Compatibilidad con DTO viejo/nuevo de trámites
        const cuilCliente = tramite.idCliente || tramite.cuilCliente;
        if (tramite.nombreCliente || tramite.apellidoCliente) {
            const nombre = tramite.nombreCliente || '';
            const apellido = tramite.apellidoCliente || '';
            const nombreCompleto = `${nombre} ${apellido}`.trim();
            return nombreCompleto || `Cliente ${cuilCliente || '-'}`;
        }

        const cliente = clientesArray.find(c => c.cuil === cuilCliente);
        return cliente ? `${cliente.nombre} ${cliente.apellido}` : `Cliente ${cuilCliente || '-'}`;
    };

    const aniosDisponibles = useMemo(() => {
        const anios = gastos.map(g => new Date(g.fecha).getFullYear().toString());
        return ['Todos', ...new Set(anios)].sort((a, b) => b - a);
    }, [gastos]);

    const clavesMesFiltradas = Object.keys(gastosAgrupados).filter(clave => {
        // clave tiene el formato YYYY-MM generado por tu función obtenerClaveMes
        const [anio, mes] = clave.split('-');
        const coincideAnio = filtroAnio === 'Todos' || anio === filtroAnio;
        const coincideMes = filtroMes === 'Todos' || mes === filtroMes;
        return coincideAnio && coincideMes;
    });


    // Agrupar gastos por mes
    const agruparGastosPorMes = (gastos) => {
        const agrupados = {};

        gastos.forEach(gasto => {
            const claveMes = obtenerClaveMes(gasto.fecha);
            if (!agrupados[claveMes]) {
                agrupados[claveMes] = {
                    nombreMes: obtenerNombreMes(gasto.fecha),
                    gastos: [],
                    total: 0
                };
            }
            agrupados[claveMes].gastos.push(gasto);
            agrupados[claveMes].total += parseFloat(gasto.monto || 0);
        });

        return Object.keys(agrupados)
            .sort((a, b) => b.localeCompare(a))
            .reduce((resultado, clave) => {
                resultado[clave] = agrupados[clave];
                return resultado;
            }, {});
    };

    // Calcular datos del mes actual
    const calcularMesActual = (gastosAgrupados) => {
        const claveActual = obtenerClaveMesActual();
        const mesActualData = gastosAgrupados[claveActual];

        if (mesActualData) {
            setMesActual({
                nombre: obtenerNombreMesActual(),
                monto: mesActualData.total,
                cantidad: mesActualData.gastos.length
            });
        } else {
            setMesActual({
                nombre: obtenerNombreMesActual(),
                monto: 0,
                cantidad: 0
            });
        }
    };

    // Cargar datos necesarios
    const cargarDatos = async () => {
        try {
            setLoading(true);

            const [gastosData, medicosData, tramitesData, clientesData] = await Promise.all([
                listarGastosMedicos(),
                listarMedicos(),
                getTramites(),
                getClientes()
            ]);

            const gastosArray = normalizarArray(gastosData);
            const medicosArray = normalizarArray(medicosData);
            const tramitesArray = normalizarArray(tramitesData);
            const clientesArray = normalizarArray(clientesData);

            setGastos(gastosArray);
            setMedicos(medicosArray);
            setTramites(tramitesArray);
            setClientes(clientesArray);

            const agrupados = agruparGastosPorMes(gastosArray);
            setGastosAgrupados(agrupados);
            calcularMesActual(agrupados);

        } catch (err) {
            setError('Error al cargar los datos');
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    // Eliminar gasto
    const handleEliminarGasto = async (idGastomedico) => {
        if (window.confirm('¿Estás seguro de que deseas eliminar este gasto?')) {
            try {
                await eliminarGastoMedico(idGastomedico);
                setSnackbar({ open: true, message: 'Gasto  eliminado correctamente', severity: 'success' });
                await cargarDatos();
            } catch (err) {
                setSnackbar({ open: true, message: 'Error al eliminar el gasto', severity: 'error' });
                console.error('Error:', err);
            }
        }
    };

    // Toggle expandir/colapsar mes
    const toggleExpandirMes = (claveMes) => {
        setMesesExpandidos(prev => ({
            ...prev,
            [claveMes]: !prev[claveMes]
        }));
    };

    // Expandir/colapsar todos los meses
    const toggleExpandirTodos = () => {
        const todosExpandidos = Object.keys(gastosAgrupados).every(clave => mesesExpandidos[clave]);

        const nuevoEstado = {};
        Object.keys(gastosAgrupados).forEach(clave => {
            nuevoEstado[clave] = !todosExpandidos;
        });

        setMesesExpandidos(nuevoEstado);
    };

    // Diálogo eliminar
    const abrirDialogoEliminar = (idGastomedico) => setDialogoEliminar({ open: true, idGastomedico });
    const cerrarDialogoEliminar = () => setDialogoEliminar({ open: false, idGastomedico: null });

    const confirmarEliminacion = async () => {
        if (dialogoEliminar.idGastomedico) {
            try {
                await eliminarGastoMedico(dialogoEliminar.idGastomedico);
                setSnackbar({ open: true, message: 'Gasto eliminado correctamente', severity: 'success' });
                cerrarDialogoEliminar();
                cargarDatos();
            } catch (error) {
                setSnackbar({ open: true, message: 'Error al eliminar el gasto', severity: 'error' });
            }
        }
    };

    // Función para abrir el modal
    const abrirModal = () => {
        setModalAbierto(true);
    };

    // Función para cerrar el modal
    const cerrarModal = () => {
        setModalAbierto(false);
    };

    // Función que se ejecuta cuando se agrega un gasto
    const handleGastoAgregado = () => {
        // Recargar la lista de gastos
        cargarDatos();
    };

    // Función para abrir modal de edición
    const abrirModalEdicion = (gasto) => {
        setGastoSeleccionado(gasto.idGastomedico);
        setModalEditarAbierto(true);
    };

    // Función para cerrar modal de edición
    const cerrarModalEdicion = () => {
        setModalEditarAbierto(false);
        setGastoSeleccionado(null);
    };

    // Función que se ejecuta cuando se actualiza un gasto
    const handleGastoActualizado = () => {
        cargarDatos(); // Recargar la lista de gastos
    };



    useEffect(() => {
        cargarDatos();
    }, []);

    return (
        <div className="area-gastos">
            <div style={{
                background: "var(--bg-page)",
                minHeight: "100vh",
                padding: "2rem"
            }}>
                <div style={{
                    background: "white",
                    borderRadius: "16px",
                    boxShadow: "var(--shadow-md)",
                    overflow: "hidden"
                }}>
                    <PageHeader
                        variante="banner"
                        icono="currency-dollar"
                        titulo="Gastos Médicos"
                        descripcion="Gestión y seguimiento de gastos por mes"
                        accion={
                            <button
                                className="btn btn-light d-flex align-items-center gap-2 fw-semibold"
                                onClick={abrirModal}
                                style={{ borderRadius: '8px', padding: '0.625rem 1.5rem' }}
                            >
                                <i className="bi bi-plus-circle me-1" />
                                Nuevo Gasto
                            </button>
                        }
                    />
                    <div className="p-4">

                    {/* Resumen del Mes Actual */}
                    <div style={{
                        background: "var(--success-bg)",
                        padding: "1.5rem",
                        borderRadius: "12px",
                        marginBottom: "1.5rem",
                        border: "2px solid #86efac"
                    }}>
                        <div className="row g-3 text-center">
                            <div className="col-md-4">
                                <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                                    <CalendarMonthIcon style={{color: "#166534"}} />
                                    <h6 className="fw-semibold mb-0" style={{color: "#166534"}}>Mes Actual</h6>
                                </div>
                                <h4 className="fw-bold text-capitalize" style={{color: "#15803d"}}>
                                    {mesActual.nombre}
                                </h4>
                            </div>
                            <div className="col-md-4">
                                <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                                    <AttachMoneyIcon style={{color: "#166534"}} />
                                    <h6 className="fw-semibold mb-0" style={{color: "#166534"}}>Total del Mes</h6>
                                </div>
                                <h4 className="fw-bold" style={{color: "#15803d"}}>
                                    ${mesActual.monto.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </h4>
                            </div>
                            <div className="col-md-4">
                                <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                                    <FolderIcon style={{color: "#166534"}} />
                                    <h6 className="fw-semibold mb-0" style={{color: "#166534"}}>Gastos del Mes</h6>
                                </div>
                                <h4 className="fw-bold" style={{color: "#15803d"}}>
                                    {mesActual.cantidad}
                                </h4>
                            </div>
                        </div>
                    </div>

                    <div className="row g-3 mb-4 p-3 align-items-end" style={{
                        background: "#fff",
                        borderRadius: "12px",
                        boxShadow: "var(--shadow-sm)",
                        border: "1px solid #e2e8f0"
                    }}>
                        <div className="col-md-4">
                            <label className="form-label fw-bold small text-secondary" style={{letterSpacing: "0.5px"}}>AÑO</label>
                            <select
                                className="form-select"
                                value={filtroAnio}
                                onChange={e => setFiltroAnio(e.target.value)}
                                style={{ border: "2px solid #e2e8f0",
                                    padding: "0.6rem 1rem", 
                                    color: "#475569",
                                    cursor: "pointer",
                                    backgroundColor: "#f8fafc" }}
                            >
                                {aniosDisponibles.map(a => (
                                    <option key={a} value={a}>{a === 'Todos' ? 'Todos los años' : a}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-bold small text-secondary" style={{letterSpacing: "0.5px"}}>MES</label>
                            <select
                                className="form-select"
                                value={filtroMes}
                                onChange={e => setFiltroMes(e.target.value)}
                                style={{ border: "2px solid #e2e8f0",
                                    padding: "0.6rem 1rem", 
                                    color: "#475569",
                                    cursor: "pointer",
                                    backgroundColor: "#f8fafc" }}
                            >
                                <option value="Todos">Todos los meses</option>
                                {mesesNombres.map(m => (
                                    <option key={m.val} value={m.val}>{m.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-4">
                            <button
                                className="btn w-100"
                                onClick={() => { setFiltroAnio('Todos'); setFiltroMes('Todos'); }}
                                style={{
                                    borderRadius: "8px",
                                    border: "2px dashed #cbd5e1",
                                    padding: "0.6rem 1rem",
                                    color: "#64748b",
                                    fontWeight: "600",
                                    background: "transparent",
                                    transition: "all 0.2s"
                                }}
                                onMouseEnter={e => { e.target.style.background = "#f1f5f9"; e.target.style.borderColor = "#94a3b8"; }}
                                onMouseLeave={e => { e.target.style.background = "transparent"; e.target.style.borderColor = "#cbd5e1"; }}
                            >
                                Limpiar Filtros
                            </button>
                        </div>
                    </div>

                    {/* Controles de Expansión */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="fw-semibold m-0" style={{color: "#374151"}}>
                            <i className="bi bi-calendar3 me-2"></i>Historial de Gastos por Mes
                        </h5>
                        <button
                            className="btn d-flex align-items-center gap-2"
                            onClick={toggleExpandirTodos}
                            style={{
                                background: "white",
                                color: "#6b7280",
                                border: "2px solid #e5e7eb",
                                borderRadius: "10px",
                                padding: "0.5rem 1rem",
                                fontWeight: 500
                            }}
                        >
                            {Object.keys(gastosAgrupados).every(clave => mesesExpandidos[clave])
                                ? <ExpandLessIcon fontSize="small" />
                                : <ExpandMoreIcon fontSize="small" />
                            }
                            {Object.keys(gastosAgrupados).every(clave => mesesExpandidos[clave])
                                ? 'Colapsar Todos'
                                : 'Expandir Todos'
                            }
                        </button>
                    </div>

                    {loading ? (
                        <div className="text-center my-5">
                            <div className="spinner-border" style={{ width: "3rem", height: "3rem", color: "#10B981" }}></div>
                            <p className="mt-3 fw-semibold text-muted">Cargando gastos médicos...</p>
                        </div>
                    ) : (
                        <>
                            {/* Lista de Gastos Agrupados por Mes */}
                            {clavesMesFiltradas.length === 0 ? (
                                <div className="text-center py-5 bg-light rounded-3 border">
                                    <p className="text-muted m-0">No se encontraron gastos para el período seleccionado.</p>
                                </div>
                            ) : (
                                clavesMesFiltradas.map(claveMes => {
                                    const grupo = gastosAgrupados[claveMes];
                                    const estaExpandido = mesesExpandidos[claveMes];
                                    const esMesActual = claveMes === obtenerClaveMesActual();

                                    return (
                                        <div key={claveMes} style={{
                                            marginBottom: "1rem",
                                            borderRadius: "12px",
                                            border: esMesActual ? "2px solid #10B981" : "2px solid #e5e7eb",
                                            overflow: "hidden",
                                            boxShadow: "var(--shadow-sm)"
                                        }}>
                                            <div
                                                onClick={() => toggleExpandirMes(claveMes)}
                                                style={{
                                                    cursor: 'pointer',
                                                    background: esMesActual ? "var(--gradient-success)" : "var(--bg-page)",
                                                    padding: "1rem 1.5rem",
                                                    color: esMesActual ? "white" : "#374151"
                                                }}
                                            >
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <h6 className="mb-0 fw-bold">
                                                        <i className="bi bi-calendar-month me-2"></i>
                                                        {grupo.nombreMes.charAt(0).toUpperCase() + grupo.nombreMes.slice(1)}
                                                        {esMesActual && <span className="badge ms-2" style={{background: "rgba(255,255,255,0.3)", color: "white"}}>Actual</span>}
                                                    </h6>
                                                    <div className="d-flex align-items-center gap-3">
                                                        <span style={{
                                                            background: esMesActual ? "rgba(255,255,255,0.2)" : "#3B82F6",
                                                            color: esMesActual ? "white" : "white",
                                                            padding: "0.25rem 0.75rem",
                                                            borderRadius: "20px",
                                                            fontSize: "0.85rem",
                                                            fontWeight: 600
                                                        }}>
                                                            {grupo.gastos.length} gasto{grupo.gastos.length !== 1 ? 's' : ''}
                                                        </span>
                                                        <span className="fw-bold" style={{fontSize: "1.1rem"}}>
                                                            ${grupo.total.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </span>
                                                        {estaExpandido ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                    </div>
                                                </div>
                                            </div>

                                            {estaExpandido && (
                                                <div className="card-body p-0">
                                                    <div className="table-responsive">
                                                        <table className="table table-hover align-middle mb-0">
                                                            <thead>
                                                            <tr style={{
                                                                background: "var(--gradient-primary-light)",
                                                                fontWeight: 600,
                                                                color: "#1e40af"
                                                            }}>
                                                                <th className="text-center" style={{ width: "12%", padding: "1rem", borderBottom: "2px solid #bae6fd" }}>Fecha</th>
                                                                <th className="text-center" style={{ width: "20%", padding: "1rem", borderBottom: "2px solid #bae6fd" }}>Cliente</th>
                                                                <th className="text-center" style={{ width: "12%", padding: "1rem", borderBottom: "2px solid #bae6fd" }}>Monto</th>
                                                                <th className="text-center" style={{ width: "20%", padding: "1rem", borderBottom: "2px solid #bae6fd" }}>Médico</th>
                                                                <th className="text-center" style={{ width: "26%", padding: "1rem", borderBottom: "2px solid #bae6fd" }}>Comentario</th>
                                                                <th className="text-center" style={{ width: "10%", padding: "1rem", borderBottom: "2px solid #bae6fd" }}>Acciones</th>
                                                            </tr>
                                                            </thead>
                                                            <tbody>
                                                            {grupo.gastos.map(gasto => (
                                                                <tr key={gasto.idGastomedico} style={{
                                                                    transition: "all 0.2s ease",
                                                                    cursor: "default"
                                                                }}>
                                                                    <td className="text-center" style={{ padding: "1rem", color: "#374151" }}>{formatearFecha(gasto.fecha)}</td>

                                                                    <td className="text-center" style={{ padding: "1rem", color: "#374151" }}>
                                                                        <span style={{ fontWeight: "bold" }}>
                                                                                {obtenerNombreClientePorTramite(gasto.idTramite)}
                                                                            </span>
                                                                    </td>
                                                                    <td className="text-center fw-bold" style={{ padding: "1rem", color: "#059669" }}>
                                                                        ${parseFloat(gasto.monto).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                    </td>
                                                                    <td className="text-center" style={{ padding: "1rem", color: "#374151" }}>
                                                                        {obtenerNombreMedico(gasto.idMedico)}
                                                                    </td>
                                                                    <td style={{ wordBreak: "break-word", padding: "1rem", color: "#6b7280" }}>
                                                                        {gasto.comentario || '-'}
                                                                    </td>

                                                                    <td className="text-center" style={{ padding: "1rem" }}>
                                                                        <div className="btn-group btn-group-sm">
                                                                            <button
                                                                                className="d-flex align-items-center gap-1"
                                                                                onClick={() => abrirModalEdicion(gasto)}
                                                                                title="Editar gasto"
                                                                                style={{
                                                                                    padding: "0.5rem 1rem",
                                                                                    border: "2px solid #3B82F6",
                                                                                    borderRadius: "8px",
                                                                                    background: "white",
                                                                                    color: "#3B82F6",
                                                                                    cursor: "pointer",
                                                                                    transition: "all 0.2s ease",
                                                                                    fontWeight: 500
                                                                                }}
                                                                                onMouseEnter={e => e.currentTarget.style.background = "#EFF6FF"}
                                                                                onMouseLeave={e => e.currentTarget.style.background = "white"}
                                                                            >
                                                                                <EditIcon fontSize="small" />
                                                                            </button>
                                                                            <button
                                                                                className="d-flex align-items-center gap-1"
                                                                                onClick={() => abrirDialogoEliminar(gasto.idGastomedico)}
                                                                                title="Eliminar gasto"
                                                                                style={{
                                                                                    padding: "0.5rem 1rem",
                                                                                    border: "2px solid #EF4444",
                                                                                    borderRadius: "8px",
                                                                                    background: "white",
                                                                                    color: "#EF4444",
                                                                                    cursor: "pointer",
                                                                                    transition: "all 0.2s ease",
                                                                                    fontWeight: 500,
                                                                                    marginLeft: "0.5rem"
                                                                                }}
                                                                                onMouseEnter={e => e.currentTarget.style.background = "#FEF2F2"}
                                                                                onMouseLeave={e => e.currentTarget.style.background = "white"}
                                                                            >
                                                                                <DeleteIcon fontSize="small" />
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </>
                    )}
                </div>
            </div>
            </div>

            {/* Modales*/}
            <ModalAgregarGastoMedico
                open={modalAbierto}
                onClose={cerrarModal}
                onGastoAgregado={handleGastoAgregado}
            />

            <ModalEditarGastoMedico
                open={modalEditarAbierto}
                onClose={cerrarModalEdicion}
                gastoId={gastoSeleccionado}
                onGastoActualizado={handleGastoActualizado}
            />

            {/* 🔸 Diálogo de Eliminación */}
            <Dialog
                open={dialogoEliminar.open}
                onClose={cerrarDialogoEliminar}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title" className="fw-bold">
                    Confirmar Eliminación
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        ¿Estás seguro de que deseas eliminar este gasto? Esta acción no se puede deshacer.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <button
                        className="btn btn-outline-secondary rounded-pill px-3"
                        onClick={cerrarDialogoEliminar}
                    >
                        Cancelar
                    </button>
                    <button
                        className="btn btn-danger rounded-pill px-3 d-flex align-items-center gap-1"
                        onClick={confirmarEliminacion}
                        autoFocus
                    >
                        <DeleteIcon fontSize="small" />
                        Eliminar
                    </button>
                </DialogActions>
            </Dialog>

            {/* 🔸 Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default ListadoGastoMedico;

